// Danışman › Müşteri profili › "KPI erişimi" editörü (düzenle / davet) — ClientKpiAccessCard'dan
// ayrıldı (≤600 satır). Portal yetki v2 §8.4: portal çekmecesiyle AYNI alt bileşenler
// (AccessSetEditor: rol, üç durumlu kategori, gösterge istisnaları, işletmeler); danışmanda kapsam
// kilidi yok (`scope = null`). Düzenlemede PUT gövdesi `expected` jetonlarını taşır; 409
// KPI_ACCESS_STALE → sakin mesaj + kart güncel kullanıcıyı yeniden yükler (sessiz yeniden deneme yok).

import { useEffect, useId, useRef, useState } from "react";
import type { ClientAdminSet, ClientEntity, ClientUser } from "@mahir/shared";
import { inviteClientKpiUser, setKpiAccessAsConsultant } from "../../api/client-admin";
import { ApiError } from "../../api/client";
import { CLIENT_KPI_ACCESS_CARD_COPY as C } from "../../lib/client-admin-copy";
import { describeAccessError } from "../../lib/client-access-errors";
import {
  type AccessDraftV2,
  buildExpected,
  draftMapFromAccessV2,
  draftsToGrants,
  newSetDraftV2,
  patchSetDraft,
  toggleSetInDraftV2,
} from "../../lib/client-access-draft-v2";
import { isValidEmail } from "../../lib/email";
import { AccessSetEditor } from "../portal/AccessSetEditor";

export type KpiEditorState = { mode: "invite" } | { mode: "edit"; user: ClientUser };

interface Props {
  clientId: string;
  sets: ClientAdminSet[];
  entities: ClientEntity[];
  state: KpiEditorState;
  onClose: () => void;
  onSaved: (user: ClientUser) => void;
  onInvited: (mailSent: boolean, existingActive: boolean) => void;
  /** 409 STALE sonrası güncel kullanıcıyı yeniden yükle. */
  onStale: () => Promise<void>;
}

export function KpiAccessEditor(props: Props): JSX.Element {
  const { clientId, sets, entities, state, onClose, onSaved, onInvited } = props;
  const isInvite = state.mode === "invite";
  const user = state.mode === "edit" ? state.user : null;
  const [draft, setDraft] = useState<AccessDraftV2>(() =>
    user ? draftMapFromAccessV2(user.kpiAccess) : new Map(),
  );
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const keepError = useRef(false);
  const titleId = useId();
  const firstRef = useRef<HTMLInputElement>(null);

  // Güncel kullanıcı geldiğinde (409 sonrası yeniden yükleme) taslak sıfırlanır, mesaj kalır.
  useEffect(() => {
    if (!user) return;
    setDraft(draftMapFromAccessV2(user.kpiAccess));
    if (keepError.current) keepError.current = false;
    else setError(null);
  }, [user]);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const emailOk = isValidEmail(email);
  const emptyEntities = [...draft.values()].some((d) => !d.allEntities && d.entities.size === 0);
  const canSubmit =
    !emptyEntities &&
    (isInvite ? email.trim().length > 3 && fullName.trim().length > 1 && !busy : !busy);

  async function handleSubmit(): Promise<void> {
    setTouched(true);
    if (isInvite && !emailOk) {
      setError(C.editor.emailInvalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const originals = new Map((user?.kpiAccess ?? []).map((a) => [a.setId, a]));
      const grants = draftsToGrants(draft, originals, null);
      if (user) {
        onSaved(
          await setKpiAccessAsConsultant(
            clientId,
            user.id,
            grants,
            buildExpected(grants, originals),
          ),
        );
      } else {
        const res = await inviteClientKpiUser(clientId, {
          email: email.trim(),
          fullName: fullName.trim(),
          ...(grants.length > 0 ? { grants } : {}),
        });
        onInvited(res.mailSent, res.existingActive);
      }
    } catch (err) {
      const info = describeAccessError(err, C.editor.genericError);
      setError(
        err instanceof ApiError && C.editor.errors[err.code]
          ? C.editor.errors[err.code]!
          : info.text,
      );
      if (info.reload) {
        keepError.current = true;
        await props.onStale().catch(() => {
          keepError.current = false;
        });
      }
    } finally {
      setBusy(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    borderColor: "var(--color-border)",
    fontFamily: "var(--font-sans)",
  };
  const originals = new Map((user?.kpiAccess ?? []).map((a) => [a.setId, a]));

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={() => !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="kpi-access-editor"
        className="rounded-xl p-6 max-w-2xl w-full mx-4 flex flex-col gap-4"
        style={{
          background: "var(--color-surface)",
          color: "var(--color-text)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <div
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "var(--color-primary)",
                fontWeight: 700,
              }}
            >
              {C.editor.kicker}
            </div>
            <h2 id={titleId} className="text-lg" style={{ fontFamily: "var(--font-display)" }}>
              {user ? user.fullName : C.editor.inviteTitle}
            </h2>
            {user ? (
              <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
                {user.email}
              </div>
            ) : (
              <p className="text-sm mt-1" style={{ color: "var(--color-text-2)" }}>
                {C.editor.inviteDescription}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={C.editor.close}
            className="text-sm px-2 py-1 rounded"
            style={{ color: "var(--color-text-3)" }}
          >
            ✕
          </button>
        </div>

        {isInvite ? (
          <div className="grid gap-3">
            <label className="block text-xs" style={{ color: "var(--color-text-2)" }}>
              {C.editor.fullName}
              <input
                ref={firstRef}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={C.editor.fullNamePlaceholder}
                disabled={busy}
                className="w-full p-2 rounded border text-sm mt-1"
                style={inputStyle}
              />
            </label>
            <label className="block text-xs" style={{ color: "var(--color-text-2)" }}>
              {C.editor.email}
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error === C.editor.emailInvalid) setError(null);
                }}
                onBlur={() => setTouched(true)}
                placeholder={C.editor.emailPlaceholder}
                disabled={busy}
                aria-invalid={touched && !emailOk && email.trim() !== "" ? true : undefined}
                className="w-full p-2 rounded border text-sm mt-1"
                style={{
                  ...inputStyle,
                  ...(touched && !emailOk && email.trim() !== ""
                    ? { borderColor: "var(--color-error)" }
                    : {}),
                }}
              />
              {touched && !emailOk && email.trim() !== "" ? (
                <span className="block mt-1" style={{ color: "var(--color-error)" }}>
                  {C.editor.emailInvalid}
                </span>
              ) : null}
            </label>
          </div>
        ) : null}

        <fieldset style={{ minWidth: 0 }}>
          <legend
            className="text-sm mb-1"
            style={{ color: "var(--color-text-2)", fontWeight: 600 }}
          >
            {C.editor.sets}
          </legend>
          {sets.length === 0 ? (
            <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
              {C.noSets}
            </div>
          ) : (
            sets.map((s) => (
              <AccessSetEditor
                key={s.id}
                set={s}
                draft={draft.get(s.id)}
                original={originals.get(s.id)}
                entities={entities}
                scope={null}
                disabled={busy}
                selfAuditorNote={false}
                onToggle={(on) =>
                  setDraft((d) =>
                    toggleSetInDraftV2(d, s.id, on, () => newSetDraftV2(s, null, entities)),
                  )
                }
                onPatch={(patch) => setDraft((d) => patchSetDraft(d, s.id, patch))}
              />
            ))
          )}
        </fieldset>
        <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.editor.ceilingNote}
        </p>

        {error ? (
          <div
            role="alert"
            className="rounded-md p-3 text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm"
            style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
          >
            {C.editor.cancel}
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="px-4 py-2 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              fontWeight: 600,
            }}
          >
            {isInvite
              ? busy
                ? C.editor.submitting
                : C.editor.submit
              : busy
                ? C.editor.saving
                : C.editor.save}
          </button>
        </div>
      </div>
    </div>
  );
}
